import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "@/ts/typings/NFC";



export function useNFC() {
	const [isReading, setIsReading] = useState<boolean>(false);
	const [data, setData] = useState<unknown>(null); // Can be string or object
	const [error, setError] = useState<string | null>(null);
	const [readId, setReadId] = useState<number>(0);
	const abortControllerRef = useRef<AbortController | null>(null);
	const hasReadRef = useRef(false);
	const readingErrorReportedRef = useRef(false);

	const nfcSupported = useMemo(() => {
		return typeof window !== "undefined" && "NDEFReader" in window;
	}, []);

	const stopNFCReading = useCallback(() => {
		const abortController = abortControllerRef.current;
		abortControllerRef.current = null;
		hasReadRef.current = false;
		abortController?.abort();
		setIsReading(false);
	}, []);

	const resumeNFCReading = useCallback(() => {
		if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) return;

		hasReadRef.current = false;
		readingErrorReportedRef.current = false;
		setError(null);
	}, []);

	const startNFCReading = useCallback(async () => {
		if (abortControllerRef.current) return;

		setError(null);
		setData(null);
		hasReadRef.current = false;
		readingErrorReportedRef.current = false;

		if (!nfcSupported) {
			setError("Web NFC is not supported here.");
			return;
		}

		const abortController = new AbortController();
		abortControllerRef.current = abortController;
		setIsReading(true);

		try {
			const ndef = new window.NDEFReader();

			ndef.onreading = (event: NDEFReadingEvent) => {
				if (abortController.signal.aborted || abortControllerRef.current !== abortController || hasReadRef.current) return;

				hasReadRef.current = true;
				readingErrorReportedRef.current = false;

				let decodedData: unknown = "";
				for (const record of event.message.records) {
					if (record.data != null) {
						try {
							const decoder = new TextDecoder(record.encoding ?? "utf-8");
							const decodedText = decoder.decode(record.data);

							// --- Logic for JSON vs String ---
							try {
								const json = JSON.parse(decodedText) as unknown;
								decodedData = json === null ? decodedText : json;
							} catch {
								decodedData = decodedText;
							}
						} catch {
							decodedData = "";
						}
					}
				}

				setError(null);
				setData(decodedData);
				setReadId((id) => id + 1);
			};

			ndef.onreadingerror = () => {
				if (abortController.signal.aborted || abortControllerRef.current !== abortController) return;

				if (hasReadRef.current) {
					hasReadRef.current = false;
					return;
				}

				if (!readingErrorReportedRef.current) {
					readingErrorReportedRef.current = true;
					setError("Tag detected but cannot be read.");
				}
			};

			await ndef.scan({ signal: abortController.signal });
		} catch (err) {
			if (abortControllerRef.current === abortController) {
				abortControllerRef.current = null;
				setIsReading(false);

				const error = err instanceof Error ? err : new Error(String(err));
				if (error.name !== "AbortError") {
					setError(`Error: ${error.message}`);
				}
			}
		}
	}, [nfcSupported]);

	// Call stopNFCReading when the component using this hook unmounts
	useEffect(() => { return () => { stopNFCReading(); }; }, [stopNFCReading]);

	return { nfcSupported, isReading, startNFCReading, stopNFCReading, resumeNFCReading, data, error, readId };
}